/** CMS-16 — platform settings over `GET/PUT /v1/cms/settings` (docs/06 §2.14). Admin only. */
import type { CmsSettingsResponse } from '@akpl/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { api } from '../api';
import { PageHeader } from '../components/layout/AppShell';
import { Button } from '../components/ui/button';
import { Field, Input, Textarea } from '../components/ui/field';
import { Badge, Panel, PanelHeader } from '../components/ui/panel';
import { Switch } from '../components/ui/switch';
import { useT } from '../i18n';
import { formatDateTime } from '../lib/format';
import { useOperatorName } from '../lib/useOperatorName';
import {
  changedKeys,
  fromForm,
  type SettingsForm,
  SettingsFormSchema,
  toForm,
} from './settingsForm';

type FlagName =
  'entitlementsEnforce' | 'drmEnforce' | 'billingCheckoutEnabled' | 'authSocialEnabled';

const FLAGS: { name: FlagName; key: string; help: string }[] = [
  { name: 'entitlementsEnforce', key: 'entitlements.enforce', help: 'entitlements' },
  { name: 'drmEnforce', key: 'drm.enforce', help: 'drm' },
  { name: 'billingCheckoutEnabled', key: 'billing.checkout_enabled', help: 'billing' },
  { name: 'authSocialEnabled', key: 'auth.social_enabled', help: 'social' },
];

export function Settings() {
  const { t, i18n } = useT();
  const queryClient = useQueryClient();
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const query = useQuery<CmsSettingsResponse>({
    queryKey: ['cms', 'settings'],
    queryFn: () => api.cmsSettings(),
  });

  const form = useForm<SettingsForm>({
    resolver: zodResolver(SettingsFormSchema),
    values: query.data ? toForm(query.data.settings) : undefined,
  });
  const { control, register, handleSubmit, reset, formState } = form;

  const mutation = useMutation({
    mutationFn: async (values: SettingsForm) => {
      const current = query.data!.settings;
      const patch = changedKeys(current, fromForm(values));
      if (Object.keys(patch).length === 0) return query.data!;
      return api.cmsSettingsPut({ body: patch });
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['cms', 'settings'], data);
      reset(toForm(data.settings));
      setSaved(true);
      setSaveError(null);
    },
    onError: (error: unknown) => {
      setSaved(false);
      setSaveError(error instanceof Error ? error.message : String(error));
    },
  });

  const onSubmit = handleSubmit((values) => mutation.mutate(values));
  const updatedByName = useOperatorName(query.data?.updatedBy);
  const disabled = !query.data || mutation.isPending;

  return (
    <>
      <PageHeader
        title={t('cms:settings.title')}
        subtitle={
          query.data?.updatedAt
            ? t('cms:settings.lastSaved', {
                time: formatDateTime(query.data.updatedAt, i18n.language),
                who: updatedByName,
              })
            : t('cms:settings.subtitle')
        }
        actions={
          <>
            <Button
              variant="secondary"
              disabled={disabled || !formState.isDirty}
              onClick={() => query.data && reset(toForm(query.data.settings))}
            >
              {t('cms:settings.discard')}
            </Button>
            <Button type="submit" form="settings-form" disabled={disabled}>
              {mutation.isPending ? t('common:states.saving') : t('cms:settings.save')}
            </Button>
          </>
        }
      />

      <form id="settings-form" onSubmit={(e) => void onSubmit(e)} className="px-6 pb-8" noValidate>
        {query.isError && (
          <p role="alert" className="mb-3 text-[13px] text-danger">
            {t('common:states.error')}
          </p>
        )}
        {saveError && (
          <p role="alert" className="mb-3 text-[13px] text-danger">
            {saveError}
          </p>
        )}
        {saved && !formState.isDirty && (
          <p role="status" className="mb-3 text-[13px] text-ok">
            {t('cms:settings.saved')}
          </p>
        )}

        <div className="grid gap-3 xl:grid-cols-[1.3fr_1fr]">
          <div className="flex flex-col gap-3">
            <Panel>
              <PanelHeader
                title={t('cms:settings.sections.general')}
                subtitle={t('cms:settings.sections.generalHint')}
              />
              <div className="flex flex-col gap-4 px-4 py-4">
                <Field
                  label={t('cms:settings.supportEmail')}
                  help={t('cms:settings.help.supportEmail')}
                  error={formState.errors.supportEmail && t('common:auth.invalidEmail')}
                >
                  {(id) => <Input id={id} type="email" {...register('supportEmail')} />}
                </Field>

                <div className="rounded-lg border border-line px-3 py-2.5">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      {/* A real <label for> — the switch is a native input (UX-209). */}
                      <label
                        htmlFor="settings-maintenance-enabled"
                        className="text-[13px] font-semibold text-ink"
                      >
                        {t('cms:settings.maintenanceEnabled')}
                      </label>
                      <p className="text-[12px] text-muted">{t('cms:settings.help.maintenance')}</p>
                    </div>
                    <Controller
                      control={control}
                      name="maintenanceEnabled"
                      render={({ field }) => (
                        <Switch
                          id="settings-maintenance-enabled"
                          checked={field.value ?? false}
                          onCheckedChange={field.onChange}
                        />
                      )}
                    />
                  </div>
                </div>

                <Field label={t('cms:settings.maintenanceTextEs')}>
                  {(id) => <Textarea id={id} {...register('maintenanceEs')} />}
                </Field>
                <Field label={t('cms:settings.maintenanceTextEn')}>
                  {(id) => <Textarea id={id} {...register('maintenanceEn')} />}
                </Field>
              </div>
            </Panel>

            <Panel>
              <PanelHeader
                title={t('cms:settings.sections.session')}
                subtitle={t('cms:settings.sections.sessionHint')}
              />
              <div className="grid gap-4 px-4 py-4 sm:grid-cols-2">
                <Field
                  label={t('cms:settings.sessionDuration')}
                  help={t('cms:settings.help.sessionDuration')}
                  error={formState.errors.sessionHours && t('common:validation.invalid')}
                >
                  {(id) => (
                    <Input id={id} type="number" min={1} max={72} {...register('sessionHours')} />
                  )}
                </Field>
                <Field
                  label={t('cms:settings.controlsTimeout')}
                  help={t('cms:settings.help.controlsTimeout')}
                  error={formState.errors.controlsTimeoutMs && t('common:validation.invalid')}
                >
                  {(id) => (
                    <Input
                      id={id}
                      type="number"
                      min={1000}
                      max={600000}
                      step={500}
                      {...register('controlsTimeoutMs')}
                    />
                  )}
                </Field>
              </div>
            </Panel>
          </div>

          <Panel className="self-start">
            <PanelHeader
              title={t('cms:settings.sections.flags')}
              subtitle={t('cms:settings.sections.flagsHint')}
            />
            <ul className="px-4 py-2">
              {FLAGS.map((flag) => (
                <li key={flag.key} className="border-b border-line py-3 last:border-0">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="flex items-center gap-2 text-[13px] font-semibold text-ink">
                        {/* The flag name IS the switch's label, associated, not merely adjacent. */}
                        <label htmlFor={`settings-flag-${flag.name}`}>{flag.key}</label>
                        <Controller
                          control={control}
                          name={flag.name}
                          render={({ field }) => (
                            <Badge tone={field.value ? 'ok' : 'neutral'}>
                              {field.value ? t('cms:settings.on') : t('cms:settings.off')}
                            </Badge>
                          )}
                        />
                      </p>
                      <p
                        id={`settings-flag-${flag.name}-help`}
                        className="mt-0.5 text-[12px] text-muted"
                      >
                        {t(`cms:settings.help.${flag.help}` as 'cms:settings.help.drm')}
                      </p>
                    </div>
                    <Controller
                      control={control}
                      name={flag.name}
                      render={({ field }) => (
                        <Switch
                          id={`settings-flag-${flag.name}`}
                          aria-describedby={`settings-flag-${flag.name}-help`}
                          checked={field.value ?? false}
                          onCheckedChange={field.onChange}
                        />
                      )}
                    />
                  </div>
                </li>
              ))}
            </ul>
            <p className="m-3 rounded-lg bg-warn/12 px-3 py-2 text-[12px] text-warn-ink">
              {t('cms:settings.warning')}
            </p>
          </Panel>
        </div>
      </form>
    </>
  );
}
